import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaFilter,
  FaMedal,
  FaSearch,
  FaTrophy,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";

import { getAllPalmares } from "./../../../services/palmaresServices";
import { getAllSections } from "./../../../services/sectionsServices";
import { formatDateFR, truncateText } from "./homeUtils";

const PalmaresTable = () => {
  const [palmaresList, setPalmaresList] = useState([]);
  const [sections, setSections] = useState([]);
  const [filteredSection, setFilteredSection] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [palmaresData, sectionsData] = await Promise.all([
          getAllPalmares(),
          getAllSections(),
        ]);

        if (!mounted) return;

        setPalmaresList(Array.isArray(palmaresData) ? palmaresData : []);
        setSections(Array.isArray(sectionsData) ? sectionsData : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des palmarès:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const orderedPalmares = useMemo(() => {
    return [...palmaresList].sort((a, b) => {
      const dateA = new Date(a.date_palmares || 0).getTime();
      const dateB = new Date(b.date_palmares || 0).getTime();
      return dateB - dateA;
    });
  }, [palmaresList]);

  const filteredPalmares = useMemo(() => {
    const q = search.trim().toLowerCase();

    return orderedPalmares.filter((palmares) => {
      const sectionId = String(
        palmares.section_id || palmares.section?.id || "",
      );

      const sectionName = String(palmares.section?.nom_section || "");
      const title = String(palmares.titre || "");
      const results = String(palmares.resultats || "");

      const matchesSection = filteredSection
        ? sectionId === String(filteredSection)
        : true;

      const matchesSearch = q
        ? `${sectionName} ${title} ${results}`.toLowerCase().includes(q)
        : true;

      return matchesSection && matchesSearch;
    });
  }, [orderedPalmares, filteredSection, search]);

  const displayedPalmares = filteredPalmares.slice(0, 10);

  const selectedSectionName = useMemo(() => {
    if (!filteredSection) return "Toutes les associations";

    const section = sections.find(
      (item) => String(item.id) === String(filteredSection),
    );

    return section?.nom_section || "Association sélectionnée";
  }, [filteredSection, sections]);

  return (
    <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Résultats"
          title="Les derniers "
          highlight="palmarès"
          subtitle="Mettez en avant les réussites des associations de la commune : compétitions, concours, distinctions, podiums ou événements marquants."
        />

        <Reveal>
          <div className="mb-8 grid gap-4 rounded-[2rem] border border-slate-200 bg-slate-50 p-4 shadow-sm dark:border-white/10 dark:bg-slate-950 md:grid-cols-[1fr_280px]">
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
              <FaSearch className="text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un titre, résultat ou association..."
                className="w-full bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
              />
            </div>

            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-slate-900">
              <FaFilter className="text-primary" />
              <select
                id="sectionFilter"
                value={filteredSection}
                onChange={(e) => setFilteredSection(e.target.value)}
                className="w-full bg-transparent text-sm font-bold text-slate-700 outline-none dark:text-white"
              >
                <option value="">Toutes les associations</option>
                {sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.nom_section}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <StatCard
              icon={FaTrophy}
              label="Palmarès affichés"
              value={displayedPalmares.length}
            />
            <StatCard
              icon={FaMedal}
              label="Résultats trouvés"
              value={filteredPalmares.length}
            />
            <StatCard
              icon={FaFilter}
              label="Filtre actif"
              value={selectedSectionName}
              small
            />
          </div>
        </Reveal>

        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
            Chargement des palmarès...
          </div>
        ) : displayedPalmares.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
            Aucun palmarès trouvé.
          </div>
        ) : (
          <>
            <Reveal>
              <div className="hidden overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950 md:block">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                        <th className="px-5 py-4">Association</th>
                        <th className="px-5 py-4">Date</th>
                        <th className="px-5 py-4">Titre</th>
                        <th className="px-5 py-4">Résultats</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 dark:divide-white/10">
                      {displayedPalmares.map((palmares) => (
                        <tr
                          key={palmares.id}
                          className="transition hover:bg-slate-50 dark:hover:bg-white/5"
                        >
                          <td className="px-5 py-4">
                            <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                              {palmares.section?.nom_section || "Association"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                            <span className="inline-flex items-center gap-2">
                              <FaCalendarAlt className="text-primary" />
                              {formatDateFR(palmares.date_palmares)}
                            </span>
                          </td>

                          <td className="px-5 py-4 font-bold text-slate-950 dark:text-white">
                            {palmares.titre}
                          </td>

                          <td className="px-5 py-4 text-slate-600 dark:text-slate-300">
                            {truncateText(palmares.resultats, 130)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Reveal>

            <div className="grid gap-4 md:hidden">
              {displayedPalmares.map((palmares, index) => (
                <Reveal key={palmares.id} delay={index * 0.04}>
                  <article className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-white/10 dark:bg-slate-950">
                    <div className="flex items-start justify-between gap-4">
                      <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                        {palmares.section?.nom_section || "Association"}
                      </span>

                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow">
                        <FaTrophy />
                      </span>
                    </div>

                    <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                      {palmares.titre}
                    </h3>

                    <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-300">
                      <FaCalendarAlt className="text-primary" />
                      {formatDateFR(palmares.date_palmares)}
                    </p>

                    <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {palmares.resultats}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

function StatCard({ icon: Icon, label, value, small = false }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-950">
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Icon />
        </span>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
            {label}
          </p>
          <p
            className={[
              "mt-1 truncate font-bold text-slate-950 dark:text-white",
              small ? "text-sm" : "text-2xl",
            ].join(" ")}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

export default PalmaresTable;
